// ---------------------------------------------------------------------------
// Dossier domain models — mirrors the database schema from 003_dossier_tables.sql
// ---------------------------------------------------------------------------

// --- Status enums ---

export enum DossierStatus {
  CREATED = 'created',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  DELETED = 'deleted',
}

export enum UploadStatus {
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  OCR_COMPLETED = 'ocr_completed',
  CLASSIFICATION_COMPLETED = 'classification_completed',
  EXTRACTING = 'extracting',
  EXTRACTION_COMPLETED = 'extraction_completed',
  COMPLETED = 'completed',
  PROCESSING_FAILED = 'processing_failed',
  OCR_FAILED = 'ocr_failed',
  CLASSIFICATION_FAILED = 'classification_failed',
  EXTRACTION_FAILED = 'extraction_failed',
  DELETED = 'deleted',
}

export enum DocumentStatus {
  CLASSIFIED = 'classified',
  EXTRACTING = 'extracting',
  EXTRACTED = 'extracted',
  EXTRACTION_FAILED = 'extraction_failed',
  REVIEWED = 'reviewed',
}

export enum PageStatus {
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  OCR_COMPLETED = 'ocr_completed',
  OCR_FAILED = 'ocr_failed',
  IGNORED = 'ignored',
}

// --- Interfaces ---

export interface Page {
  oid: string;
  document_oid: string;
  upload_oid: string;
  page_number: number;
  image_s3_path: string;
  ocr_s3_path: string | null;
  status: PageStatus;
  created_at: string;
}

/**
 * A native SWIFT MT / ISO 20022 MX message uploaded directly (.mt/.rje/.xml).
 * Such documents skip OCR and LLM extraction entirely, so they have zero
 * pages and no page images — the review screen shows the message itself
 * instead of a scan.
 */
export interface NativeMessage {
  /** 'mt' = SWIFT MT/FIN text, 'mx' = ISO 20022 XML. */
  format: 'mt' | 'mx';
  /**
   * The original message content, unaltered — also what is sent downstream.
   * Read server-side from the encrypted original; omitted if that read fails.
   */
  raw?: string;
  /** MT number (e.g. '700') or ISO 20022 id (e.g. 'pacs.008.001.11'). */
  message_type?: string | null;
  /** MT only. */
  sender_bic?: string | null;
  receiver_bic?: string | null;
  /**
   * MT only: canonical field names resolved from the raw tags, used by the
   * UCP-600/ISBP examination engine. Includes mt_fields, the verbatim ordered
   * tag list used for the friendly display.
   */
  parsed_fields?: Record<string, any> | null;
  s3_key?: string;
}

export interface Document {
  oid: string;
  upload_oid: string;
  document_type: string;
  confidence: number | null;
  status: DocumentStatus;
  extraction_result?: Record<string, any> | null;
  page_count: number;
  pages?: Page[];
  created_at: string;
  updated_at: string;
  /**
   * Set only for native SWIFT MT / ISO 20022 documents (page_count = 0).
   * When present, there is no page image and no extraction to run.
   */
  native_message?: NativeMessage | null;
  /** Summary fields from list endpoint (no full extraction_result). */
  extraction_tokens?: number | null;
  extraction_model?: string | null;
  extraction_error?: { error_code: string; error_detail: string; error_model: string } | null;
  /** Originality marking: true = original, false = copy, null = not assessed. */
  is_original?: boolean | null;
  /** Last downstream export outcome: 'sent', 'rejected', or null (never exported). */
  downstream_status?: string | null;
  /** Rejection message from the downstream endpoint when downstream_status === 'rejected'. */
  downstream_error?: string | null;
}

export interface Upload {
  oid: string;
  dossier_oid: string;
  original_filename: string;
  file_type: string;
  status: UploadStatus;
  created_at: string;
  updated_at: string;
}

/** A distinguished name, as far as the panel displays it. */
export interface CertificateName {
  commonName: string | null;
  organization: string | null;
  organizationalUnit: string | null;
  country: string | null;
  serialNumber: string | null;
}

/** One X.509 certificate in the signer's chain. */
export interface CertificateDetail {
  subject: CertificateName;
  issuer: CertificateName;
  serial: string | null;
  validFrom: string | null;
  validTo: string | null;
  /** KeyUsage bits the certificate actually asserts (RFC 5280 §4.2.1.3). */
  keyUsage: string[];
  extendedKeyUsage: string[];
  sha256: string | null;
  /** DER bytes, base64 — the Export button builds the .cer from this. */
  derB64: string | null;
}

/** eIDAS qualification verdict. Absent (null) means NOT DETERMINED — the EU
 *  trusted lists were unavailable or the chain did not reach one. That is a
 *  different statement from "not qualified" and must render differently. */
export interface QualificationDetail {
  qualified: boolean;
  /** QC_ESIGN (Annex I), QC_ESEAL (Annex III) or QC_WEB (Annex IV). */
  qcType: string | null;
  qcKeySecurity: string | null;
  /** Private key declared to reside in a qualified signature creation device. */
  qscd: boolean;
  /** Name of the trusted-list service entry the verdict rests on. */
  serviceName: string | null;
  serviceType: string | null;
}

/** What is known about revocation. checkedOnline is false while live OCSP is
 *  disabled server-side, so the panel must never imply a real-time check. */
export interface RevocationDetail {
  checkedOnline: boolean;
  revoked: boolean;
  details: {
    caRevoked: boolean;
    revocationDate: string | null;
    revocationReason: string | null;
  } | null;
}

/** State of the EU Trusted Lists at the moment the verification ran. */
export interface TrustedListsStatus {
  enabled: boolean;
  ready: boolean;
  /** When the List of Trusted Lists was last successfully fetched. */
  last_checked: string | null;
  source: string;
  stats?: {
    certificate_authorities: number;
    timestamp_authorities: number;
    service_errors: number;
    download_failures: string[];
    territories: string | string[];
    build_seconds: number;
  };
}

/** One embedded signature, verified server-side (dd_signatures — pyHanko). */
export interface SignatureDetail {
  status: 'VALID' | 'WARNING' | 'INVALID';
  /** PDF signature field name, e.g. "Signature1". */
  field: string | null;
  signerCommonName: string | null;
  signerOrganization: string | null;
  signingTime: string | null;
  /** Non-null only when a timestamp token covers the signature (B-T and up). */
  timestampTime: string | null;
  coverage: string | null;
  docMdpOk: boolean | null;
  intact: boolean;
  valid: boolean;
  trusted: boolean;
  /** "B-B" (signer's own clock) or "B-T" (timestamped). LTV is never claimed. */
  adesLevel: string | null;
  certificate: CertificateDetail | null;
  /** Validated chain, leaf first. Empty when no path could be built. */
  chain: CertificateDetail[];
  qualification: QualificationDetail | null;
  revocation: RevocationDetail | null;
  error?: string | null;
}

/** Badge-ready signature status for one file — GET /dossier/{id}/uploads/{id}/signature.
 *
 *  Fields below `signatures` are null on records verified before they were
 *  recorded; the panel shows "not available", never "not present". */
export interface SignatureStatus {
  overallStatus: 'NONE' | 'VALID' | 'WARNING' | 'INVALID';
  signatureCount: number;
  /** When the stored verification ran — the panel's "Last checked". */
  verifiedAt: string | null;
  trustedLists: TrustedListsStatus | null;
  signatures: SignatureDetail[];
}

/** A batch/ZIP upload returns one SignatureStatus per contained file. */
export interface FileSignatureStatus extends SignatureStatus {
  filename: string | null;
}

export interface BatchSignatureStatus {
  files: FileSignatureStatus[];
}

export interface Dossier {
  oid: string;
  external_id: string;
  user_oid: string;
  status: DossierStatus;
  processing_mode: string;
  batch_mode?: boolean;
  batch_model?: string | null;
  batch_output_format?: string | null;
  /** When true, document examination (UCP-600 checks) is enabled on this dossier. */
  trade_finance?: boolean;
  /** Tradefinance: sent as the customerClientKey header on downstream export. */
  customer_key?: string | null;
  /** Tradefinance: sent as the accountClientKey header on downstream export. */
  account_key?: string | null;
  /** Tradefinance: 2-letter code sent as the fromCountry header on downstream export. */
  from_country?: string | null;
  upload_count?: number;
  uploads?: Upload[];
  created_at: string;
  updated_at: string;
  /** RSA-OAEP encrypted DEK, hex-encoded. Present when per-user encryption is active. */
  encrypted_dek?: string | null;
  /** HKDF salt, hex-encoded. Stored alongside the encrypted DEK. */
  dek_salt?: string | null;
  /** Keypair slot ('A' or 'B') used to wrap the DEK. */
  key_slot?: string | null;
  /** Server-side flag: true when the dd_dek cookie already has a valid-format DEK for this dossier. */
  dek_available?: boolean;
  /**
   * Set when the document set changed after the last examination run (D4):
   * the checks and discrepancies on screen describe a document set that no
   * longer exists. Null means current, or never examined. Cleared by Auto-verify.
   */
  examination_stale_at?: string | null;
}

export interface ExtractionResult {
  document_id: string;
  status: string;
  extraction_result: Record<string, unknown> | string | null;
  document_type?: string | null;
  page_count?: number | null;
  /**
   * Native SWIFT MT / ISO 20022 documents carry their content here instead of
   * extraction_result — they are parsed deterministically, never sent to an
   * LLM, and have no page images.
   */
  native_message?: NativeMessage | null;
}

export type CheckStatus = 'pending' | 'ok' | 'fail' | 'na';

export interface ModelTier {
  key: string;
  label: string;
  model: string;
  multiplier: number;
}

export interface DossierCheck {
  oid: string;
  dossier_oid: string;
  check_id: string;
  check_category: string;
  check_description: string;
  status: CheckStatus;
  notes: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  /** UCP-ISBP catalog rows (ucpisbp_NNN) this check implements — explainability. */
  catalog_refs?: string[];
}

/** Status of a discrepancy alert. */
export type AlertStatus = 'pending' | 'false_alarm' | 'violation' | 'resolved';

/** How a discrepancy was detected. */
export type AlertSource = 'engine' | 'auto' | 'manual';

/** A discrepancy (dossier_alert) — engine/auto-detected or user-added. */
export interface DossierAlert {
  oid: string;
  dossier_oid: string;
  rule_name: string | null;
  description: string;
  doc_a_type: string | null;
  field_a: string | null;
  value_a: string | null;
  doc_b_type: string | null;
  field_b: string | null;
  value_b: string | null;
  status: AlertStatus;
  source: AlertSource;
  created_by: string | null;
  resolved_by: string | null;
  resolved_notes: string | null;
  created_at: string;
  updated_at: string;
}

/** Auto-vs-manual discrepancy KPI for a dossier. */
export interface DiscrepancyKpi {
  auto: number;
  manual: number;
  total: number;
}

/** An entry in the UCP-ISBP discrepancy catalog (for manual-add selection). */
export interface DiscrepancyCatalogEntry {
  id: string;
  description: string;
  ucp_ref: string;
  isbp_ref: string;
  templated: boolean;
}
